<script setup>
/* eslint semi: 1 */

// Package imports
import { ref, computed, inject, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import { DateTime } from 'luxon';

// Component imports
import SignInEmail from '@/components/common/sign-in/sign-in-email-tmpl.vue';
import SignInPassword from '@/components/common/sign-in/sign-in-password-tmpl.vue';
import AuthSuccess from '@/components/common/sign-in/auth-success-tmpl.vue';
import SignInUpdateDetails from '@/components/common/sign-in/sign-in-update-details-tmpl.vue';

import { t } from '@/i18n/i18ns';
import { ssoGetMigrateAccountFlagFromUrl } from '@/globals/external-provider-auth-utils';

// Mixin imports
import AuthMixin from '@/components/mixins/auth-mixin';
import EventBus from '@/components/mixins/event-bus-mixin';

// Injects
const $signedInUserDetails = inject('$signedInUserDetails');
const $utils = inject('$utils');
const $dataStore = inject('$dataStore');
const $router = inject('$routerInstance');

// Proxy is the instance of mixins defined in defineOptions
const { proxy } = getCurrentInstance();

const route = useRoute();

// Constants
const SIGN_IN_CONTENTS = {
  EMAIL: 'email',
  PASSWORD: 'password',
  SET_PASSWORD: 'set-password',
  UPDATE_DETAILS: 'update-details',
  MIGRATE_TO_SSO_WARNING: 'migrate-to-sso-prompt',
  MIGRATE_TO_SSO_VERIFICATION: 'migrate-to-sso-verification',
  MIGRATE_TO_SSO_CONFIRMATION: 'migrate-to-sso-confirmation',
  SUCCESS: 'success'
};

// Component Options added so we can get access to mixins inside <script setup>
defineOptions({
  mixins: [AuthMixin]
});

// Props
defineProps({
  isPageMini: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['userNotConfirmed', 'redirectToSignUp', 'redirectToForgotPassword', 'migrationVerified']);

// Variables
const signInContent = ref(SIGN_IN_CONTENTS.EMAIL);
const email = ref('');
const stage = ref(null);
const cognitoUser = ref(null);

// Computed properties
const isLoginInvite = computed(() => $router?.currentRoute?.value?.query?.['lform'] === 'explicit');
// This implies that with hasSocialSignIn, self sign up
// is allowed. Because social sign in performs a sign up
// behind the scenes, so if self sign up is not allowed,
// social sign in should not be allowed either.
const hasSocialSignIn = computed(() => {
  if ($utils.isSignUpDisallowed() || $utils.isPageForEnterprise()) {
    return false;
  }

  return !isLoginInvite.value;
});

// Methods
async function handleSignIn (params = {}) {
  proxy.sendSignedInEvent();
  if (!$signedInUserDetails) {
    proxy.getUserDetailsFromBackend();
  }

  // Close signIn card and loader
  $utils.animate('#signin', 'animated fadeOutLeft');
  $utils.mainLoader(false);

  // Check terms of service
  // FIXME: Check if we can refactory this checkTermsOfService function. Like do not pass callback
  await proxy.checkTermsOfServices({
    callback: () => {
      const targetPageAfterLogin = localStorage.getItem('targetPageAfterLogin');

      $dataStore.setStore({ lastLogin: DateTime.utc().toISO() }); // Set last login date in data store

      if (targetPageAfterLogin !== null) {
        $router.push(`/${targetPageAfterLogin}`, () => {});
        localStorage.removeItem('targetPageAfterLogin');
      } else {
        const redirectTo = params?.redirectTo ? params.redirectTo : '/loginok';
        $router.push(redirectTo, () => {});
      }
    }
  });
}

/**
 * Content is not exactly the same, it will be broken down into
 * smaller functions here for better readability.
 *
 * This function handles the common actions after a user has been
 * authenticated.
 */
function handleCommonActionAfterAuthentication () {
  // Account migration to SSO
  const migrateAccountFlag = ssoGetMigrateAccountFlagFromUrl(route.query);

  if (migrateAccountFlag && migrateAccountFlag.showConfirm) {
    $utils.mainLoader(false);
    signInContent.value = SIGN_IN_CONTENTS.SUCCESS;
    emit('migrationVerified');
  } else {
    handleSignIn();
  }
}

async function emailSubmit (payload) {
  stage.value = payload.stage;
  email.value = payload.email;

  // When verifying user email to migrate to SSO, confirm that cognitoStage is the same
  const migrateAccountFlag = ssoGetMigrateAccountFlagFromUrl(route.query);
  if (migrateAccountFlag && migrateAccountFlag.showConfirm) {
    const externalAccountCognitoStage = migrateAccountFlag.cognitoStage;
    const externalAccountEmail = migrateAccountFlag.email;
    const isStageMismatch = stage.value !== externalAccountCognitoStage;
    const isEmailMismatch = email.value.toLowerCase() !== externalAccountEmail.toLowerCase();

    if (isStageMismatch || isEmailMismatch) {
      let message = '';
      if (isStageMismatch) {
        message = t('errors.cannotMigrateAccountInAnotherRegion');
      } else if (isEmailMismatch) {
        message = t('errors.invalid.emailMismatch');
      }

      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message
        },
        callbacks: {}
      });

      return;
    }
  }
  signInContent.value = SIGN_IN_CONTENTS.PASSWORD;
}

function successSubmit (payload) {
  if (payload?.cognitoUser) {
    cognitoUser.value = payload.cognitoUser;
  }

  if (signInContent.value === SIGN_IN_CONTENTS.SET_PASSWORD) {
    signInContent.value = SIGN_IN_CONTENTS.UPDATE_DETAILS;

    return;
  } else if (signInContent.value === SIGN_IN_CONTENTS.UPDATE_DETAILS) {
    signInContent.value = SIGN_IN_CONTENTS.UPDATE_DETAILS;
  } else if (isLoginInvite.value || payload?.isNewPasswordRequired) {
    signInContent.value = SIGN_IN_CONTENTS.SET_PASSWORD;

    return;
  } else {
    signInContent.value = SIGN_IN_CONTENTS.SUCCESS;
  }

  if (payload?.success) {
    handleCommonActionAfterAuthentication();
  }
}

function userNotConfirmed (payload) {
  emit('userNotConfirmed', {
    ...payload,
    stage
  });
}

defineExpose({
  commonActionAfterAuthentication: handleCommonActionAfterAuthentication
});
</script>

<template>
  <div
    class="sign-in-card gradient-card default py-6! px-6! flex flex-col items-center max-[1919px]:w-[276px]! max-[1919px]:min-h-[478px]! min-[1920px]:w-[356px]! min-[1920px]:min-h-[629px]!"
    :class="{ 'no-background': $utils.isPageForLicenses() }"
  >
    <!-- Title -->
    <h4 class="font-semibold uppercase text-white text-center text-[28px] leading-[100%] py-3 break-words w-full">
      <template v-if="signInContent === SIGN_IN_CONTENTS.SET_PASSWORD">
        {{ $t('label.setNewPassword') }}
      </template>
      <template v-else-if="signInContent === SIGN_IN_CONTENTS.UPDATE_DETAILS">
        {{ $t('label.checkDetails') }}
      </template>
      <template v-else>
        {{ $t('label.login') }}
      </template>
    </h4>

    <sign-in-email
      v-if="signInContent === SIGN_IN_CONTENTS.EMAIL"
      :has-social-sign-in="hasSocialSignIn"
      :is-sign-in-mini="isPageMini"
      @email-submit="emailSubmit"
      @redirect-to-sign-up="$emit('redirectToSignUp')"
      @external-sign-in-success="handleCommonActionAfterAuthentication"
    />
    <sign-in-password
      v-else-if="signInContent === SIGN_IN_CONTENTS.PASSWORD || signInContent === SIGN_IN_CONTENTS.SET_PASSWORD"
      :email="email"
      :stage="stage"
      :is-login-invite="isLoginInvite"
      :is-sign-in-mini="isPageMini"
      @success-submit="successSubmit"
      @edit-email="signInContent = SIGN_IN_CONTENTS.EMAIL"
      @user-not-confirmed="userNotConfirmed"
      @reset-password="$emit('redirectToForgotPassword', email)"
    />
    <sign-in-update-details
      v-else-if="signInContent === SIGN_IN_CONTENTS.UPDATE_DETAILS"
      :is-sign-in-mini="isPageMini"
      :cognito-user="cognitoUser"
      @success-submit="successSubmit"
    />
    <auth-success v-else-if="signInContent === SIGN_IN_CONTENTS.SUCCESS" />
  </div>
</template>
